import type { ReactNode } from 'react'

// Field — ATC-128 / `ATC-ADR-018`.
//
// WHY THIS EXISTS, and it is not tidiness. Every input in this console is
// styled ONLY by ancestor selector: `.toolbar input`, `.inline-form input`,
// `.modal-body input`. A control that sits in none of those three renders
// COMPLETELY UNSTYLED -- default browser chrome on a dense operator surface --
// and nothing in CI notices, because there is no rule to violate. `Field`
// gives a control an `.rx-field` ancestor of its own, so a form can live
// anywhere.
//
// STRUCTURAL LABEL ASSOCIATION: the control is rendered INSIDE the `<label>`.
// No `useId`, no `htmlFor` plumbing, no `cloneElement` to inject an id --
// `getByLabelText` works because the DOM says so rather than because two
// generated strings happen to match.
//
// A CHECKBOX SET CANNOT USE THIS. A `<label>` may be associated with exactly
// one labelable control; wrapping several checkboxes in one label associates
// the text with the first and silently orphans the rest. That is what
// `CheckboxGroup` is for, and why it uses `<fieldset>` / `<legend>` instead.

export interface FieldProps {
  /** The visible label text. Rendered inside the `<label>`, above the control. */
  label: ReactNode
  /**
   * Static guidance shown under the control. Use for a format hint or a
   * consequence, never for an error — errors use `error`, which is announced.
   */
  hint?: ReactNode
  /**
   * A validation message. Rendered with `role="alert"` so it is announced when
   * it appears, and it replaces the hint rather than stacking with it.
   */
  error?: ReactNode
  /** Marks the field visually and for assistive tech. */
  required?: boolean
  /** Exactly one labelable control. */
  children: ReactNode
}

export function Field({ label, hint, error, required = false, children }: FieldProps) {
  return (
    <label className="rx-field">
      <span className="rx-field-label">
        {label}
        {required && (
          <span className="rx-field-required" aria-hidden="true">
            {' *'}
          </span>
        )}
        {required && <span className="rx-visually-hidden"> (required)</span>}
      </span>
      {children}
      {error ? (
        <span className="rx-field-error" role="alert">
          {error}
        </span>
      ) : (
        hint !== undefined && <span className="rx-field-hint">{hint}</span>
      )}
    </label>
  )
}
